import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  ChevronDown, ChevronRight, Home, Brain, Zap,
  Briefcase, ClipboardList, Truck, Package, DollarSign, Building2,
  Users, ShieldCheck, Settings, Lock, Activity, Workflow
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { PRODUCT } from "../../lib/productConfig";

/* ═══════════════════════════════════════════════════════════════════
   ENTERPRISE NAVIGATION V2.0 — THREE-LAYER ARCHITECTURE
   Layer 1: Mission Control (always visible)
   Layer 2: Business Applications (role-filtered)
   Layer 3: Enterprise Platform (administrators only)
   ═══════════════════════════════════════════════════════════════════ */

// ── SUPER ADMIN BYPASS ─────────────────────────────────────────────
const SUPER_ADMIN_EMAILS = ["md@energiprogroup.com", "admin@energiprogroup.com"];

// ── ROLE DEFINITIONS ───────────────────────────────────────────────
type UserRole =
  | "super_admin"
  | "ceo"
  | "coo"
  | "cfo"
  | "vp_commercial"
  | "bdm"
  | "finance_controller"
  | "accountant"
  | "procurement_manager"
  | "project_manager"
  | "qhse_manager"
  | "hr_manager"
  | "engineer"
  | "auditor"
  | "staff";

// ── BUSINESS APP → ROLE ACCESS ─────────────────────────────────────
// DEF-027: "auditor" added to every section below. The real IAM catalog
// (role-006 "Auditor", GET /api/iam/roles) grants { module: "All Modules",
// actions: ["view"], scope: "all" } -- read-only visibility across every
// module -- but this map previously had no "auditor" entry at all, so an
// Auditor fell through mapBackendRole() to the generic "staff" bucket,
// which isn't listed here either: assigning someone the Auditor role gave
// them zero Business Application menu items, contradicting what the role
// claims to grant. Auditor is intentionally left OFF Layer 3 (Enterprise
// Platform, `isAdmin`-gated) -- it's read-only business visibility, not
// admin authority.
const APP_ACCESS: Record<string, UserRole[]> = {
  commercial: ["super_admin", "ceo", "coo", "cfo", "vp_commercial", "bdm", "project_manager", "auditor"],
  projects: ["super_admin", "ceo", "coo", "vp_commercial", "project_manager", "engineer", "auditor"],
  procurement: ["super_admin", "ceo", "coo", "cfo", "procurement_manager", "project_manager", "auditor"],
  inventory: ["super_admin", "ceo", "coo", "procurement_manager", "project_manager", "auditor"],
  finance: ["super_admin", "ceo", "cfo", "finance_controller", "accountant", "auditor"],
  assets: ["super_admin", "ceo", "coo", "cfo", "finance_controller", "auditor"],
  hr: ["super_admin", "ceo", "coo", "hr_manager", "qhse_manager", "auditor"],
  qhse: ["super_admin", "ceo", "coo", "qhse_manager", "project_manager", "auditor"],
};

// ── ROLE DETECTION ─────────────────────────────────────────────────
function mapBackendRole(backendRole: string): UserRole {
  const n = (backendRole || "").toLowerCase().trim();
  if (n.includes("super") || n.includes("admin")) return "super_admin";
  // DEF-027: checked before the "ceo"/"md" branch below so a real role named
  // "Auditor" (role-006) doesn't fall through to "staff" (zero module
  // access) -- see the APP_ACCESS comment above.
  if (n.includes("auditor")) return "auditor";
  if (n.includes("ceo") || n.includes("managing director") || n.includes("md")) return "ceo";
  if (n.includes("coo") || n.includes("operations director") || n.includes("operations controller") || (n.includes("operations") && n.includes("manager"))) return "coo";
  if (n.includes("cfo") || n.includes("finance director")) return "cfo";
  if (n.includes("vp") && n.includes("commercial")) return "vp_commercial";
  if (n.includes("bdm") || n.includes("business development")) return "bdm";
  if (n.includes("controller") || n.includes("financial controller")) return "finance_controller";
  if (n.includes("accountant") || n.includes("accounting")) return "accountant";
  if (n.includes("procurement") || n.includes("supply chain")) return "procurement_manager";
  if (n.includes("project manager") || n.includes("pm")) return "project_manager";
  if (n.includes("qhse") || n.includes("hse") || n.includes("quality") || n.includes("ims manager")) return "qhse_manager";
  if (n.includes("hr") || n.includes("human resource")) return "hr_manager";
  if (n.includes("engineer") || n.includes("technician")) return "engineer";
  return "staff";
}

// ── NAV STRUCTURES ─────────────────────────────────────────────────
interface NavItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  // DEF-038 (2026-09-19): a group-divider row used to be modeled as a real
  // NavItem with path:"" (e.g. "─── Existing IMS ───"), which rendered as
  // an actual clickable NavLink and also broke the section's active-state
  // check (`location.pathname.startsWith("" + "/")` matches every route,
  // so the QHSE section showed as "active" on every page in the app).
  // Use divider:true for a non-clickable group label instead.
  divider?: boolean;
}

interface NavSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}

// ═══ LAYER 1: MISSION CONTROL ═══
const MISSION_CONTROL_ITEMS: NavItem[] = [
  { label: "Mission Control", path: "/mission-control", icon: <Home className="h-4 w-4" /> },
  { label: "Action Center", path: "/mission-control/actions", icon: <Zap className="h-4 w-4" /> },
  // DEF-041 (2026-09-20): "Notifications" duplicated "Notification Center"
  // under Workflow & Documents (same component, two sidebar entries). Kept
  // the Workflow & Documents entry since the page itself is approval-
  // focused; this Mission Control entry was removed.
  { label: "AI Advisor", path: "/mission-control/ai-advisor", icon: <Brain className="h-4 w-4" /> },
];

// ═══ LAYER 2: BUSINESS APPLICATIONS ═══
const BUSINESS_APPS: NavSection[] = [
  {
    id: "commercial",
    label: "Commercial",
    icon: <Briefcase className="h-4 w-4" />,
    items: [
      { label: "Opportunities", path: "/app/commercial/opportunities" },
      { label: "Proposals", path: "/app/commercial/proposals" },
      { label: "Clients", path: "/app/commercial/clients" },
      { label: "Client POs", path: "/app/commercial/client-pos" },
      { label: "Client Payments", path: "/app/commercial/client-payments" },
      { label: "Pipeline Forecast", path: "/app/commercial/pipeline-forecast" },
      { label: "Sales Performance", path: "/app/commercial/sales-performance" },
    ],
  },
  {
    id: "projects",
    label: "Projects & Operations",
    icon: <ClipboardList className="h-4 w-4" />,
    items: [
      { label: "Projects", path: "/app/projects/list" },
      { label: "Timesheets", path: "/app/projects/timesheets" },
      { label: "Project Costs", path: "/app/projects/costs" },
      { label: "Project P&L", path: "/app/projects/pnl" },
      { label: "Milestones", path: "/app/projects/milestones" },
      { label: "Delivery Notes", path: "/app/projects/delivery-notes" },
    ],
  },
  {
    id: "procurement",
    label: "Procurement",
    icon: <Truck className="h-4 w-4" />,
    items: [
      { label: "⚡ Source-to-Approve", path: "/app/procurement/s2a" },
      { label: "Requisitions", path: "/app/procurement/s2a/requisitions" },
      { label: "RFQ Engine", path: "/app/procurement/s2a/rfqs" },
      { label: "Evaluation", path: "/app/procurement/s2a/evaluation" },
      { label: "📊 Intelligence (EAE)", path: "/app/procurement/s2a/intelligence" },
      { label: "Purchase Orders", path: "/app/procurement/purchase-orders" },
      { label: "Vendor Invoices", path: "/app/finance/vendor-invoices" },
      { label: "Quotations", path: "/app/procurement/quotations" },
      { label: "GRN (Delivery)", path: "/app/procurement/grn" },
      { label: "Vendors", path: "/app/procurement/vendors" },
      { label: "Vendor Scoring", path: "/app/procurement/vendor-scoring" },
      { label: "PO Export", path: "/app/procurement/po-export" },
    ],
  },
  {
    id: "inventory",
    label: "Inventory & Warehouse",
    icon: <Package className="h-4 w-4" />,
    items: [
      { label: "Inventory List", path: "/app/inventory" },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    icon: <DollarSign className="h-4 w-4" />,
    items: [
      { label: "Dashboard", path: "/app/finance" },
      { label: "Invoices", path: "/app/finance/invoices" },
      { label: "Proforma Invoices", path: "/app/finance/proforma-invoices" },
      { label: "Credit Notes", path: "/app/finance/credit-notes" },
      { label: "AR & Collections", path: "/app/finance/aging-receivables" },
      { label: "Cash Management", path: "/app/finance/cash-management" },
      { label: "P&L Statement", path: "/app/finance/pl-statement" },
      { label: "Balance Sheet", path: "/app/finance/balance-sheet" },
      { label: "Cash Flow", path: "/app/finance/cash-flow" },
      { label: "Trial Balance", path: "/app/finance/trial-balance" },
      { label: "Journal Entries", path: "/app/finance/journal-entries" },
      { label: "Expense & Budget", path: "/app/finance/expense-budget" },
      { label: "FX Rates", path: "/app/finance/fx-rates" },
      { label: "ZATCA Onboarding", path: "/app/finance/zatca-onboarding" },
    ],
  },
  {
    id: "assets",
    label: "Enterprise Assets",
    icon: <Building2 className="h-4 w-4" />,
    items: [
      { label: "Asset Management", path: "/app/assets/management" },
      { label: "Project Accounting", path: "/app/assets/project-accounting" },
      { label: "Project Portfolio", path: "/app/assets/project-portfolio" },
    ],
  },
  {
    id: "hr",
    label: "People & HR",
    icon: <Users className="h-4 w-4" />,
    items: [
      { label: "Dashboard", path: "/app/hr" },
      { label: "Organization", path: "/app/hr/org-structure" },
      { label: "Employees", path: "/app/hr/employees" },
      { label: "Training", path: "/app/hr/training" },
      { label: "Compliance", path: "/app/hr/compliance" },
      { label: "User Management", path: "/app/hr/user-management" },
    ],
  },
  {
    id: "qhse",
    label: "QHSE",
    icon: <ShieldCheck className="h-4 w-4" />,
    items: [
      { label: "Operational Execution", path: "", divider: true },
      { label: "Safety Dashboard", path: "/app/qhse/operational-dashboard" },
      { label: "Operations & Gates", path: "/app/qhse/operations" },
      { label: "Incidents & Safety", path: "/app/qhse/incident-hub" },
      { label: "Compliance Hub", path: "/app/qhse/compliance-hub" },
      { label: "Governance & Records", path: "", divider: true },
      { label: "Documents", path: "/app/qhse/documents" },
      { label: "Records", path: "/app/qhse/records" },
      { label: "Audits", path: "/app/qhse/audits" },
      { label: "CAPA", path: "/app/qhse/capa" },
      { label: "Management Reviews", path: "/app/qhse/management-reviews" },
      { label: "ISO Readiness", path: "/app/qhse/iso-readiness" },
      { label: "KPIs", path: "/app/qhse/kpis" },
    ],
  },
];

// ═══ LAYER 3: ENTERPRISE PLATFORM (Admin Only) ═══
const PLATFORM_SECTIONS: NavSection[] = [
  {
    id: "platform_config",
    label: "Configuration Center",
    icon: <Settings className="h-4 w-4" />,
    items: [
      { label: "Config Center", path: "/platform/configuration" },
      { label: "Master Data Office", path: "/platform/master-data" },
    ],
  },
  {
    id: "platform_workflow",
    label: "Workflow & Documents",
    icon: <Workflow className="h-4 w-4" />,
    items: [
      { label: "Document Control", path: "/platform/document-control" },
      { label: "Notification Center", path: "/platform/notifications" },
    ],
  },
  {
    id: "platform_security",
    label: "Security & Audit",
    icon: <Lock className="h-4 w-4" />,
    items: [
      { label: "Security Center", path: "/platform/security" },
      { label: "Audit Center", path: "/platform/audit" },
      { label: "Approval Governance", path: "/platform/approval-governance" },
      { label: "Email Configuration", path: "/app/hr/email-auth" },
    ],
  },
  {
    id: "platform_ops",
    label: "Operations & Monitoring",
    icon: <Activity className="h-4 w-4" />,
    items: [
      { label: "Release Center", path: "/platform/releases" },
      { label: "Digital Twin", path: "/platform/digital-twin" },
      { label: "Operational Certification", path: "/platform/certification" },
      { label: "Backup & DR", path: "/platform/backup-dr" },
      { label: "Platform Monitoring", path: "/platform/monitoring" },
    ],
  },
];

// ── SIDEBAR SECTION COMPONENT ──────────────────────────────────────
function SidebarSection({ section }: { section: NavSection }) {
  const location = useLocation();
  const isActive = section.items.some(
    item => !item.divider && (location.pathname === item.path || location.pathname.startsWith(item.path + "/"))
  );
  const [expanded, setExpanded] = useState(isActive);

  return (
    <div className="mb-0.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-slate-800 text-white"
            : "text-slate-400 hover:text-white hover:bg-slate-800/50"
        }`}
      >
        {section.icon}
        <span className="flex-1 text-left text-xs">{section.label}</span>
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {expanded && (
        <div className="ml-4 mt-0.5 space-y-0.5">
          {section.items.map((item, i) =>
            item.divider ? (
              <div
                key={`divider-${i}`}
                className="px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600"
              >
                {item.label}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `block px-3 py-1 rounded text-xs transition-colors ${
                    isActive
                      ? "bg-blue-600/20 text-blue-400 font-medium"
                      : "text-slate-500 hover:text-slate-300"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ── MAIN SIDEBAR COMPONENT ─────────────────────────────────────────
export function Sidebar() {
  const { user, apiClient } = useAuth();
  const [userRole, setUserRole] = useState<UserRole>("staff");
  const [roleLoaded, setRoleLoaded] = useState(false);

  const isSuperAdmin = user?.email && SUPER_ADMIN_EMAILS.includes(user.email.toLowerCase());

  useEffect(() => {
    if (!apiClient || !user) return;
    if (isSuperAdmin) {
      setUserRole("super_admin");
      setRoleLoaded(true);
      return;
    }
    // NOTE: this used to call /api/iam/my-role, which doesn't exist anywhere
    // in the backend — that request 404'd for every account except the two
    // hardcoded SUPER_ADMIN_EMAILS above, silently fell into the catch below,
    // and defaulted everyone else to "staff" (hiding almost the entire
    // Business Applications menu regardless of their real role). /api/me
    // already exists, is already called elsewhere, and returns the same
    // dbUser row that has both system_role (the coarse Super
    // Admin/Executive Admin/... bucket) and iam_role_id (the fine-grained
    // business title — "Sales Manager", "Operations Manager", etc. — the
    // same catalog UserManagement.tsx uses). Prefer system_role for the
    // admin-tier accounts and the granular IAM role name otherwise, since
    // that's what mapBackendRole's substring matching was actually written
    // to recognize.
    const fetchRole = async () => {
      try {
        const res = await apiClient("/api/me");
        const d = res?.data;
        if (d?.systemRole === "Super Admin" || d?.isAdmin) {
          setUserRole("super_admin");
        } else if (d?.iamRoleId != null) {
          // DEF-021: the IAM role catalog (Super Admin, Sales Manager, etc.)
          // is now a real, seeded /api/iam/roles table instead of a
          // hardcoded frontend array — look the name up there directly.
          const wantedId = `role-${String(d.iamRoleId).padStart(3, "0")}`;
          const rolesRes = await apiClient("/api/iam/roles").catch(() => null);
          const roleName = (rolesRes?.data || []).find((r: any) => r.id === wantedId)?.name;
          setUserRole(roleName ? mapBackendRole(roleName) : mapBackendRole(d?.systemRole || ""));
        } else if (d?.systemRole) {
          setUserRole(mapBackendRole(d.systemRole));
        } else {
          setUserRole("staff");
        }
      } catch {
        setUserRole("staff");
      } finally {
        setRoleLoaded(true);
      }
    };
    fetchRole();
  }, [apiClient, user, isSuperAdmin]);

  const isAdmin = isSuperAdmin || userRole === "super_admin" || userRole === "ceo";

  const visibleApps = useMemo(() => {
    if (isSuperAdmin || userRole === "super_admin") return BUSINESS_APPS;
    return BUSINESS_APPS.filter(section => {
      const allowed = APP_ACCESS[section.id];
      return allowed?.includes(userRole);
    });
  }, [userRole, isSuperAdmin]);

  return (
    <aside className="w-64 h-screen bg-slate-900 border-r border-slate-800 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex h-14 items-center justify-center border-b border-slate-800 flex-shrink-0 px-3">
        <img src="/assets/energipro-solutions-logo.png" alt="EnergiPro Solutions" className="h-9 w-auto" />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1 scrollbar-thin">
        {/* ═══ LAYER 1: MISSION CONTROL ═══ */}
        <div className="mb-3">
          <p className="px-3 mb-1 text-[9px] font-bold text-slate-500 uppercase tracking-widest">
            Mission Control
          </p>
          {MISSION_CONTROL_ITEMS.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              // BUG FIX (2026-09-21): without `end`, react-router's NavLink
              // treats "/mission-control" as active for ANY nested path too
              // (e.g. "/mission-control/actions", "/mission-control/ai-advisor"),
              // since by default it matches on path prefix, not exact path.
              // That made "Mission Control" stay highlighted at the same time
              // as "Action Center" or "AI Advisor" was clicked -- two
              // overlapping blue bars in the sidebar. `end` restricts each of
              // these top-level links to matching only its own exact path.
              end
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                }`
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </div>

        {/* ═══ LAYER 2: BUSINESS APPLICATIONS ═══ */}
        <div className="mb-3">
          <p className="px-3 mb-1 text-[9px] font-bold text-slate-500 uppercase tracking-widest">
            Business Applications
          </p>
          {visibleApps.map(section => (
            <SidebarSection key={section.id} section={section} />
          ))}
        </div>

        {/* ═══ LAYER 3: ENTERPRISE PLATFORM (Admin Only) ═══ */}
        {isAdmin && (
          <div className="mb-3">
            <p className="px-3 mb-1 text-[9px] font-bold text-slate-500 uppercase tracking-widest">
              Enterprise Platform
            </p>
            {PLATFORM_SECTIONS.map(section => (
              <SidebarSection key={section.id} section={section} />
            ))}
          </div>
        )}

        {/* Role indicator */}
        {roleLoaded && !isAdmin && (
          <div className="mt-2 px-3">
            <div className="text-[10px] text-slate-600 bg-slate-800/50 rounded px-2 py-1.5 text-center">
              Access: <span className="text-slate-400 font-medium capitalize">{userRole.replace(/_/g, " ")}</span>
            </div>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="border-t border-slate-800 px-3 py-2 flex-shrink-0">
        <p className="text-[9px] text-slate-600 text-center leading-relaxed">
          {PRODUCT.name} — {PRODUCT.edition}<br />
          <span className="text-slate-700">{PRODUCT.fullVersion}</span>
        </p>
      </div>
    </aside>
  );
}
